import { AlertsAcknowledgementRequest } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { type EntityUpdate, optimisticEntities } from 'shared/normalized_cache';
import { acknowledgeAlertsByIds } from 'utils/alert';
import { pluralize } from 'utils/string';
import { type QueueRow } from '../queue/queueRowKey';
import { ROW_ERRORS_META, useRowErrorReporter } from '../queue/useRowErrors';

export type AlertEntityUpdate = Extract<EntityUpdate, { kind: 'alert' }>;

/** Entity updates rendering the Alerts as acknowledged now. */
export function acknowledgedAlertUpdates(
    alertIds: readonly string[],
): AlertEntityUpdate[] {
    const acknowledgedAt = new Date();
    return alertIds.map((id) => ({
        kind: 'alert',
        id,
        fields: { IsAcknowledged: true, TimeAcknowledged: acknowledgedAt },
    }));
}

/**
 * Acknowledges Alerts, rendering them acknowledged everywhere while saving. They stay
 * that way after saving, without refetching Mission Control's queues: rows whose
 * triggers are all acknowledged show as resolved until the user refreshes.
 *
 * Failures are reported on `row`, the queue row the Alerts are listed in.
 */
export function useAcknowledgeAlerts(row: QueueRow) {
    const rowErrors = useRowErrorReporter();
    return useMutation({
        mutationKey: ['mission-control', 'acknowledge-alerts'],
        meta: { name: 'acknowledge-alerts', ...ROW_ERRORS_META },
        mutationFn: (alertIds: readonly string[]) =>
            acknowledgeAlertsByIds(
                new AlertsAcknowledgementRequest({ AlertIds: [...alertIds] }),
            ),
        onMutate: (alertIds) => optimisticEntities(acknowledgedAlertUpdates(alertIds)),
        onSuccess: () => rowErrors.clear(row.key, 'acknowledge'),
        onError: (error, alertIds) =>
            rowErrors.report({
                row,
                source: 'acknowledge',
                summary: `Failed to acknowledge ${alertIds.length} ${pluralize(alertIds.length, 'alert')}`,
                error,
            }),
    });
}
